import { defineStore } from 'pinia'
import {
  getCompanyProfile,
  saveCompanyDraft,
  discardCompanyDraft,
  publishCompanyProfile,
} from '@/api/company/profile'
import type { CompanyProfile } from '@/types/company'

interface CompanyProfileState {
  profile: CompanyProfile | null
  isLoading: boolean
  error: string | null
}

export const useCompanyProfileStore = defineStore('companyProfile', {
  state: (): CompanyProfileState => ({
    profile: null,
    isLoading: false,
    error: null
  }),

  actions: {
    async fetchProfile() {
      this.isLoading = true
      this.error = null

      try {
        const { data } = await getCompanyProfile()
        const normalizedProfile = this.normalizeProfile(data)

        this.profile = normalizedProfile

      } catch (err: any) {
        console.error('Error fetching company profile:', err)
        this.error = err.response?.data?.error || 'Failed to load profile'
      } finally {
        this.isLoading = false
      }
    },
    async saveDraft(payload: Partial<CompanyProfile>) {
      this.isLoading = true
      this.error = null

      try {
        const { data } = await saveCompanyDraft(payload)

        // Normalize backend response (in case backend returns updated draft)
        const normalizedProfile = this.normalizeProfile(data)
        this.profile = normalizedProfile
      } catch (err: any) {
        console.error('❌ Error saving draft:', err)
        this.error = err.response?.data?.error || 'Failed to save draft'
        throw err
      } finally {
        this.isLoading = false
      }
    },
    async discardDraft() {
      this.isLoading = true
      this.error = null

      try {
        const { data } = await discardCompanyDraft()
        const normalizedProfile = this.normalizeProfile(data)
        this.profile = normalizedProfile

        return normalizedProfile
      } catch (err: any) {
        console.error('Error discarding draft:', err)
        this.error = err.response?.data?.error || 'Failed to discard draft'
        throw err
      } finally {
        this.isLoading = false
      }
    },
    async publishProfile(payload: Partial<CompanyProfile>) {
      this.isLoading = true
      this.error = null

      try {
        const { data } = await publishCompanyProfile(payload)
        const normalizedProfile = this.normalizeProfile(data)
        this.profile = normalizedProfile

        return normalizedProfile
      } catch (err: any) {
        console.error('Error publishing profile:', err)
        this.error = err.response?.data?.error || 'Failed to publish profile'
        throw err
      } finally {
        this.isLoading = false
      }
    },
    normalizeProfile(raw: any): CompanyProfile {
      const parsed = { ...raw }

      // List of fields stored as JSON strings in DB
      const jsonFields = [
        'commodities',
        'software_used',
        'key_people',
        'key_projects',
        'recent_key_hires',
        'job_links',
        'social_links',
        'recent_capital_raisings',
        'benefits',
      ]

      for (const key of jsonFields) {
        if (typeof parsed[key] === 'string') {
          try {
            parsed[key] = JSON.parse(parsed[key])
          } catch {
            parsed[key] = []
          }
        }
      }

      const toSafeString = (value: unknown): string => (typeof value === 'string' ? value : '').trim()
      const splitLegacyName = (value: unknown) => {
        const fullName = toSafeString(value)
        if (!fullName) return { first_name: '', last_name: '' }
        const parts = fullName.split(/\s+/)
        const first_name = parts.shift() ?? ''
        const last_name = parts.join(' ')
        return { first_name, last_name }
      }

      if (!Array.isArray(parsed.key_people)) {
        parsed.key_people = []
      } else {
        parsed.key_people = parsed.key_people.map((entry: any) => {
          const first_name = toSafeString(
            entry?.first_name ?? entry?.firstName ?? entry?.first ?? ''
          )
          const last_name = toSafeString(
            entry?.last_name ?? entry?.lastName ?? entry?.last ?? ''
          )
          const role = toSafeString(entry?.role ?? entry?.title ?? entry?.position)
          const role_start_date = toSafeString(
            entry?.role_start_date ?? entry?.roleStartDate ?? entry?.start_date
          )

          if (first_name || last_name) {
            return { first_name, last_name, role, role_start_date }
          }

          const legacy = splitLegacyName(entry?.name)
          return { first_name: legacy.first_name, last_name: legacy.last_name, role, role_start_date }
        })
      }

      if (!Array.isArray(parsed.job_links)) {
        parsed.job_links = []
      } else {
        parsed.job_links = parsed.job_links
          .map((entry: any) => ({
            type: toSafeString(entry?.type).toLowerCase(),
            title: toSafeString(entry?.title),
            description: toSafeString(entry?.description),
            url: toSafeString(entry?.url),
          }))
          .filter((entry: any) =>
            ['linkedin', 'seek', 'other'].includes(entry.type) && entry.url !== ''
          )
      }

      if (!Array.isArray(parsed.social_links)) {
        parsed.social_links = []
      } else {
        parsed.social_links = parsed.social_links
          .map((entry: any) => ({
            type: toSafeString(entry?.type).toLowerCase(),
            url: toSafeString(entry?.url),
          }))
          .filter((entry: any) =>
            ['linkedin', 'facebook', 'instagram', 'x', 'youtube'].includes(entry.type) &&
            entry.url !== ''
          )
      }

      if (!Array.isArray(parsed.recent_key_hires)) {
        parsed.recent_key_hires = []
      } else {
        parsed.recent_key_hires = parsed.recent_key_hires.map((entry: any) => ({
          name: toSafeString(entry?.name),
          role: toSafeString(entry?.role ?? entry?.title),
          date: toSafeString(entry?.date),
          background: toSafeString(entry?.background),
          summary: toSafeString(entry?.summary),
          url: toSafeString(entry?.url),
        }))
      }

      if (!Array.isArray(parsed.recent_capital_raisings)) {
        parsed.recent_capital_raisings = []
      } else {
        parsed.recent_capital_raisings = parsed.recent_capital_raisings.map((entry: any) => ({
          date: toSafeString(entry?.date),
          amount: toSafeString(entry?.amount),
          label: toSafeString(entry?.label ?? entry?.type),
          summary: toSafeString(entry?.summary ?? entry?.notes),
          url: toSafeString(entry?.url),
        }))
      }

      if (!Array.isArray(parsed.benefits)) {
        parsed.benefits = []
      } else {
        parsed.benefits = parsed.benefits.map((entry: any) => ({
          heading: toSafeString(entry?.heading ?? entry?.title),
          description: toSafeString(entry?.description ?? entry?.summary),
          source: toSafeString(entry?.source),
          added_at: toSafeString(entry?.added_at ?? entry?.date),
        }))
      }

      return parsed as CompanyProfile
    },
  }
})
